import { Component, Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Die Bühne – die 3D-Szene aus der Vorlage.
 *
 * Das ist der Roboter aus dem Spline-Bauteil von 21st.dev, dieselbe Szene wie
 * in der Referenz. Vorher stand hier ein handgezeichnetes SVG; es war eine
 * Annäherung und sah auch so aus. Eine gerenderte 3D-Szene lässt sich mit
 * zwei Dutzend Rechtecken nicht nachbauen.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was das kostet und wie es eingedämmt ist
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Die Spline-Laufzeit ist gross und sie ist WebGL. Beides wäre ein Problem,
 * wenn sie einfach mitgeladen würde – deshalb drei Vorkehrungen:
 *
 *   `lazy` + `Suspense`: die Laufzeit liegt in einem eigenen Bündel und ist
 *   im Erstbündel der Seite nicht enthalten. Wer die Preisseite öffnet und
 *   nie bis zum Fuss scrollt, lädt sie nie.
 *
 *   Ein IntersectionObserver mit 400 Pixeln Vorlauf hängt die Szene erst ein,
 *   wenn der Bereich in die Nähe kommt. Ohne das liefe die Renderschleife ab
 *   dem ersten Bildaufbau dauerhaft mit und machte Klicks weiter oben träge –
 *   ein Fehler, den man nicht sieht, sondern nur spürt.
 *
 *   Unter `prefers-reduced-motion` wird WebGL NIE geladen. Dort steht
 *   stattdessen das Zeichen ruhig. Eine sich drehende 3D-Figur ist genau das,
 *   wogegen diese Einstellung gesetzt wird.
 *
 * Die Szene wird von prod.spline.design geholt. Das ist eine Abhängigkeit von
 * einem fremden Dienst: ist er nicht erreichbar, bleibt die Fläche leer. Sie
 * trägt deshalb keine Information – alles, was auf der Seite gesagt werden
 * muss, steht daneben in Text.
 */

const Spline = lazy(() => import('@splinetool/react-spline'))

/* Die Szene des Bauteils. Sie gehört zum Spline-Bauteil von 21st.dev und ist
   dessen öffentliches Beispiel – nicht das Markenzeichen einer fremden Seite. */
const SZENE = 'https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode'

export function Markenbuehne({
  gross = false,
  mitWort = false
}: {
  /** Auf der Kontaktseite grösser als im Fuss. */
  gross?: boolean
  /** Wortmarke und Unterzeile unter der Szene – wie in der Vorlage. */
  mitWort?: boolean
}): JSX.Element {
  const feld = useRef<HTMLDivElement>(null)
  const [ruhig, setRuhig] = useState(true)
  const [nah, setNah] = useState(false)
  /*
    Wenn die Szene nicht kommt, steht hier nicht nichts.

    prod.spline.design ist ein fremder Dienst. Ist er langsam, gesperrt oder
    weg, bliebe sonst ein leeres Feld von 420 Pixeln stehen – und das sieht
    aus wie ein Fehler, nicht wie eine Fläche. Nach zwölf Sekunden ohne
    Canvas tritt das ruhende Zeichen an seine Stelle, und `onError` macht es
    sofort, wenn der Ladefehler schon vorher feststeht.
  */
  const [gescheitert, setGescheitert] = useState(false)

  useEffect(() => {
    if (!nah || gescheitert) return
    const uhr = setTimeout(() => {
      if (!feld.current?.querySelector('canvas')) setGescheitert(true)
    }, 12000)
    return () => clearTimeout(uhr)
  }, [nah, gescheitert])

  /* Erst nach dem Einhängen prüfen – vorher weiss die Seite nicht, wie sie
     bedient wird. */
  useEffect(() => {
    setRuhig(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  useEffect(() => {
    const el = feld.current
    if (!el || ruhig) return
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) {
          setNah(true)
          beobachter.disconnect()
        }
      },
      { rootMargin: '400px' }
    )
    beobachter.observe(el)
    return () => beobachter.disconnect()
  }, [ruhig])

  return (
    <div className={gross ? 'rp-mb rp-mb-gross' : 'rp-mb'}>
      <div className="rp-mb-feld" ref={feld}>
        {ruhig || gescheitert ? (
          <RuhigesZeichen />
        ) : nah ? (
          <Fehlergrenze aus={<RuhigesZeichen />}>
            <Suspense fallback={<Laden />}>
              <Spline
                scene={SZENE}
                className="rp-mb-szene"
                onError={() => setGescheitert(true)}
              />
            </Suspense>
          </Fehlergrenze>
        ) : (
          <Laden />
        )}
      </div>

      {mitWort && (
        <div className="rp-mb-wort">
          <strong>Raporto</strong>
          <small>Administration, die mitarbeitet.</small>
        </div>
      )}
    </div>
  )
}

/**
 * Eine Fehlergrenze um die Szene.
 *
 * `Suspense` wartet auf ein Bündel, es fängt keine Fehler. Scheitert der
 * Abruf von prod.spline.design – gesperrtes Netz, Dienst weg, Firmen-Proxy –,
 * wirft der träge Import, und OHNE diese Grenze reisst er die ganze Seite mit:
 * React hängt den Baum bis zur nächsten Grenze aus, und die nächste wäre die
 * Wurzel. Gemessen kam dabei eine Kontaktseite von null Pixeln Höhe heraus.
 *
 * Eine Verzierung darf niemals eine Seite abschiessen. Hier ist die Grenze,
 * an der sie es nicht tut.
 */
class Fehlergrenze extends Component<{ aus: ReactNode; children: ReactNode }, { hin: boolean }> {
  constructor(props: { aus: ReactNode; children: ReactNode }) {
    super(props)
    this.state = { hin: false }
  }

  static getDerivedStateFromError(): { hin: boolean } {
    return { hin: true }
  }

  render(): ReactNode {
    return this.state.hin ? this.props.aus : this.props.children
  }
}

/** Ein Ring, der sich dreht, solange die Szene noch nicht da ist. */
function Laden(): JSX.Element {
  return (
    <div className="rp-mb-laden" aria-hidden="true">
      <span />
    </div>
  )
}

/**
 * Der Platz der Szene unter reduzierter Bewegung.
 *
 * Nicht leer: eine leere Fläche von 400 Pixeln sieht aus wie ein Fehler. Ein
 * ruhendes Zeichen sieht aus wie eine Entscheidung.
 */
function RuhigesZeichen(): JSX.Element {
  return (
    <svg viewBox="0 0 300 300" className="rp-mb-ruhig" aria-hidden="true">
      <defs>
        <radialGradient id="mbRuheSchein" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(141,115,255,0.3)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
      </defs>
      <circle cx="150" cy="150" r="148" fill="url(#mbRuheSchein)" />
      <circle
        cx="150"
        cy="150"
        r="96"
        fill="none"
        stroke="rgba(185,206,255,0.24)"
        strokeWidth="1"
        strokeDasharray="4 9"
      />
      <ellipse
        cx="150"
        cy="150"
        rx="120"
        ry="46"
        fill="none"
        stroke="rgba(141,115,255,0.4)"
        strokeWidth="1.2"
      />
      <ellipse
        cx="150"
        cy="150"
        rx="46"
        ry="120"
        fill="none"
        stroke="rgba(255,142,219,0.4)"
        strokeWidth="1.2"
      />
    </svg>
  )
}
